import MenuIcon from '@mui/icons-material/Menu';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { Link, useRouterState } from '@tanstack/react-router';
import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationMenu } from '../../features/notifications';
import { settingsTabPath } from '../../features/settings/routes';
import { stateColor } from '../../theme';
import { isNavItemActive, type HeaderNavItem } from './navigation';
import { NavigationDrawer } from './NavigationDrawer';
import { ProfileMenu } from './ProfileMenu';

export type { HeaderNavItem };

export type HeaderProps = {
    navItems?: HeaderNavItem[];
    // Current partner credits balance shown in the toolbar pill.
    creditsBalance?: number;
    // Optional slot rendered immediately to the left of the notification bell (e.g. the language switcher).
    endSlot?: ReactNode;
    // The bell polls a partner-scoped endpoint, so it stays off until there is a partner to poll for.
    showNotifications?: boolean;
};

// Below this the toolbar collapses to the mobile app bar and everything it drops moves into the drawer.
const DESKTOP = 'md';

export function Header({ navItems, creditsBalance, endSlot, showNotifications = true }: HeaderProps): ReactElement {
    const { t } = useTranslation();
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const [menuOpen, setMenuOpen] = useState(false);

    const resolvedNavItems = useMemo<HeaderNavItem[]>(
        () =>
            navItems ?? [
                { label: t('NAV.NEW_REQUESTS'), to: '/new-requests' },
                { label: t('NAV.OPEN_REQUESTS'), to: '/open-requests' },
            ],
        [navItems, t],
    );

    return (
        <Box component="header" sx={{ bgcolor: 'background.paper' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    maxWidth: 1200,
                    mx: 'auto',
                    px: { xs: 2, [DESKTOP]: 3 },
                    py: 2,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, [DESKTOP]: 3 }, flex: 1 }}>
                    <IconButton
                        onClick={() => setMenuOpen(true)}
                        aria-label={t('HEADER.MENU_ARIA')}
                        aria-expanded={menuOpen}
                        sx={{ display: { xs: 'inline-flex', [DESKTOP]: 'none' } }}>
                        <MenuIcon />
                    </IconButton>

                    <Box
                        component="img"
                        src="/houzy_logo.svg"
                        alt={t('HEADER.LOGO_ALT')}
                        sx={{ height: 40, width: 112, objectFit: 'contain' }}
                    />

                    <Box
                        component="nav"
                        sx={{ display: { xs: 'none', [DESKTOP]: 'flex' }, gap: 2 }}
                        aria-label={t('HEADER.PRIMARY_NAV')}>
                        {resolvedNavItems.map((item) => {
                            const active = isNavItemActive(item, pathname);
                            return (
                                <Button
                                    key={item.to}
                                    component={Link}
                                    to={item.to}
                                    aria-current={active ? 'page' : undefined}
                                    sx={{
                                        color: active ? 'primary.main' : 'text.primary',
                                        bgcolor: active ? (theme) => stateColor(theme, 'hover') : 'transparent',
                                        letterSpacing: '0.15px',
                                        px: 2,
                                        py: 0.75,
                                        '&:hover': {
                                            bgcolor: active
                                                ? (theme): string => stateColor(theme, 'selected')
                                                : 'action.hover',
                                        },
                                    }}>
                                    {item.label}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: { xs: 1, [DESKTOP]: 2 }, alignItems: 'center' }}>
                    {creditsBalance !== undefined && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            component={Link}
                            to={settingsTabPath('credits')}
                            startIcon={<TollOutlinedIcon />}
                            aria-label={t('HEADER.CREDITS_ARIA', { count: creditsBalance })}
                            sx={{ minWidth: 0, px: { xs: 1.5, [DESKTOP]: 2 } }}>
                            <Box component="span" sx={{ display: { xs: 'none', [DESKTOP]: 'inline' } }}>
                                {t('HEADER.CREDITS_LABEL', { count: creditsBalance })}
                            </Box>
                            <Box component="span" sx={{ display: { xs: 'inline', [DESKTOP]: 'none' } }}>
                                {creditsBalance}
                            </Box>
                        </Button>
                    )}
                    <Box
                        sx={{
                            display: { xs: 'none', [DESKTOP]: 'flex' },
                            gap: 2,
                            alignItems: 'center',
                        }}>
                        {endSlot}
                    </Box>
                    {showNotifications && <NotificationMenu />}
                    <Box sx={{ display: { xs: 'none', [DESKTOP]: 'inline-flex' } }}>
                        <ProfileMenu />
                    </Box>
                </Box>
            </Box>
            <Divider />

            <NavigationDrawer open={menuOpen} onClose={() => setMenuOpen(false)} navItems={resolvedNavItems} />
        </Box>
    );
}
